import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useDataSync } from './useDataSync'
import { toast } from 'sonner'

export interface PatientAuditLog {
  id: string
  action: string
  resource_type: string
  resource_id: string
  user_id: string
  user_name: string
  user_role: string
  patient_id: string
  ip_address: string
  user_agent: string
  timestamp: string
  details: Record<string, any>
  created_at: string
}

export interface AuditFilters {
  startDate?: string
  endDate?: string
  action?: string
  resourceType?: string
  userId?: string
}

export function usePatientAuditLogs() {
  const { user } = useAuth()
  const { registerRevalidation } = useDataSync()
  const [logs, setLogs] = useState<PatientAuditLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<AuditFilters>({})

  const fetchPatientAuditLogs = async (customFilters?: AuditFilters) => {
    if (!user) {
      setError('Usuário não autenticado')
      return
    }

    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('audit_logs')
        .select(`
          id,
          action,
          resource_type,
          resource_id,
          user_id,
          user_name,
          user_role,
          patient_id,
          ip_address,
          user_agent,
          timestamp,
          details,
          created_at
        `)
        .eq('patient_id', user.id) // Filtrar apenas logs relacionados ao paciente logado
        .order('created_at', { ascending: false })

      // Aplicar filtros
      const activeFilters = customFilters || filters
      
      if (activeFilters.startDate) {
        query = query.gte('created_at', activeFilters.startDate)
      }
      
      if (activeFilters.endDate) {
        query = query.lte('created_at', activeFilters.endDate)
      }
      
      if (activeFilters.action) {
        query = query.eq('action', activeFilters.action)
      }
      
      if (activeFilters.resourceType) {
        query = query.eq('resource_type', activeFilters.resourceType)
      }
      
      if (activeFilters.userId) {
        query = query.eq('user_id', activeFilters.userId)
      }

      const { data, error: fetchError } = await query.limit(100)

      if (fetchError) {
        console.error('Erro ao buscar logs de auditoria:', fetchError)
        setError('Erro ao carregar logs de auditoria')
        toast.error('Erro ao carregar logs de auditoria')
        return
      }

      setLogs(data || [])
    } catch (err) {
      console.error('Erro inesperado ao buscar logs:', err)
      setError('Erro inesperado ao carregar logs')
      toast.error('Erro inesperado ao carregar logs')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = (newFilters: AuditFilters) => {
    setFilters(newFilters)
    fetchPatientAuditLogs(newFilters)
  }

  const clearFilters = () => {
    setFilters({})
    fetchPatientAuditLogs({})
  }

  const refreshLogs = () => {
    fetchPatientAuditLogs(filters)
  }

  const getActionDescription = (action: string): string => {
    const descriptions: Record<string, string> = {
      'view_medical_record': 'Visualizou prontuário médico',
      'download_medical_record': 'Baixou prontuário médico',
      'view_document': 'Visualizou documento médico',
      'download_document': 'Baixou documento médico',
      'create_medical_record': 'Criou novo prontuário',
      'update_medical_record': 'Atualizou prontuário',
      'delete_medical_record': 'Excluiu prontuário',
      'upload_document': 'Fez upload de documento',
      'share_record': 'Compartilhou prontuário',
      'access_patient_data': 'Acessou dados do paciente',
      'view_patient_profile': 'Visualizou perfil do paciente',
      'update_patient_profile': 'Atualizou perfil do paciente'
    }
    return descriptions[action] || action
  }

  const getResourceTypeDescription = (resourceType: string): string => {
    const descriptions: Record<string, string> = {
      'medical_record': 'Prontuário Médico',
      'medical_document': 'Documento Médico',
      'patient_profile': 'Perfil do Paciente',
      'lab_result': 'Resultado de Exame',
      'prescription': 'Prescrição Médica',
      'consultation': 'Consulta Médica',
      'diagnosis': 'Diagnóstico',
      'treatment': 'Tratamento'
    }
    return descriptions[resourceType] || resourceType
  }

  const getUserRoleDescription = (role: string): string => {
    const descriptions: Record<string, string> = {
      'doctor': 'Médico',
      'nurse': 'Enfermeiro(a)',
      'admin': 'Administrador',
      'patient': 'Paciente',
      'specialist': 'Especialista',
      'technician': 'Técnico'
    }
    return descriptions[role] || role
  }

  const exportLogsToCSV = () => {
    if (logs.length === 0) {
      toast.warning('Não há logs para exportar')
      return
    }

    const headers = [
      'Data/Hora',
      'Ação',
      'Tipo de Recurso',
      'Profissional',
      'Função',
      'Detalhes'
    ]

    const csvData = logs.map(log => [
      new Date(log.created_at).toLocaleString('pt-BR'),
      getActionDescription(log.action),
      getResourceTypeDescription(log.resource_type),
      log.user_name || 'Não informado',
      getUserRoleDescription(log.user_role),
      JSON.stringify(log.details || {})
    ])

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `meus_acessos_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast.success('Logs exportados com sucesso')
  }

  // Carregar logs automaticamente quando o usuário estiver disponível
  useEffect(() => {
    if (user) {
      fetchPatientAuditLogs()
    }
  }, [user])

  // Registrar função de revalidação com o sistema de sincronização
  useEffect(() => {
    if (!user) return;

    const unregister = registerRevalidation(async () => {
      console.log('🔄 [usePatientAuditLogs] Revalidação automática após refresh...');
      try {
        await fetchPatientAuditLogs(filters);
        console.log('✅ [usePatientAuditLogs] Revalidação de logs concluída com sucesso');
      } catch (error) {
        console.error('❌ [usePatientAuditLogs] Erro na revalidação:', error);
      }
    });

    return unregister;
  }, [user, registerRevalidation, filters]);

  return {
    logs,
    loading,
    error,
    filters,
    applyFilters,
    clearFilters,
    refreshLogs,
    exportLogsToCSV,
    getActionDescription,
    getResourceTypeDescription,
    getUserRoleDescription
  }
}